// @section 1 Clients · PortfolioView — active-client roster.
//
// Sources from /api/hq/delivery/engagements (existing endpoint). Each row is
// a client with engagement status, owner, last-activity. Click → /clients/:id.
//
// Data shape is loose on purpose: hq_delivery is being cleaned up after this
// module ships; for now, accept whatever the API returns and render
// defensively so missing fields don't crash the row.

import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { listEngagements } from '../../api';
import { useIsMobile } from '../../lib/useIsMobile';
import { FeedScroll, FeedHeader, FeedEmpty, FeedCard } from '../../components/MobileFeed';

// @subsection 1.1 Types

type Engagement = {
  id?: string;
  client_id?: string;
  client_name?: string;
  status?: string;
  started_at?: string;
  owner?: string;
  // Tolerate any other fields the API may return.
  [k: string]: any;
};

// @subsection 1.2 Component

export default function PortfolioView() {
  const [rows, setRows] = useState<Engagement[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // @block 1.3 Phone fork: the roster table becomes a feed of client cards.
  const isMobile = useIsMobile();

  useEffect(() => {
    let cancelled = false;
    listEngagements()
      .then((r: any) => !cancelled && setRows(Array.isArray(r) ? r : []))
      .catch((e) => !cancelled && setErr(String(e)));
    return () => { cancelled = true; };
  }, []);

  if (isMobile) {
    return (
      <FeedScroll>
        <FeedHeader
          title="Clients"
          summary={rows ? `${rows.length} active engagement${rows.length === 1 ? '' : 's'}` : 'Loading…'}
        />
        {rows === null && !err && <FeedEmpty>Loading…</FeedEmpty>}
        {err && <FeedEmpty>Failed to load engagements: {err}</FeedEmpty>}
        {rows && rows.length === 0 && !err && <FeedEmpty>No active client engagements yet.</FeedEmpty>}
        {rows && rows.map((r, i) => {
          const id = r.client_id || r.id || String(i);
          const meta = [r.owner || r.owner_name, r.started_at ? `started ${new Date(r.started_at).toLocaleDateString()}` : null]
            .filter(Boolean).join(' · ');
          return (
            <FeedCard
              key={id}
              title={r.client_name || r.name || '(unnamed)'}
              chip={r.status || undefined}
              meta={meta || undefined}
              to={`/clients/${id}`}
            />
          );
        })}
      </FeedScroll>
    );
  }

  return (
    <div className="px-4 py-5 md:px-7 md:py-6 max-w-6xl">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-1">Atlas / Clients</div>
        <h1 className="text-3xl font-bold text-paper-100">Clients</h1>
        <p className="text-sm text-muted mt-1">
          Active engagements. Pipeline drops a deal here once it hits Won. Click a row to open the client workspace.
        </p>
      </div>

      {/* @block 1.3 Loading */}
      {rows === null && !err && (
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-6 animate-pulse">
          <div className="h-4 w-48 bg-ink-800 rounded mb-3" />
          <div className="h-3 w-full bg-ink-800/60 rounded mb-2" />
          <div className="h-3 w-3/4 bg-ink-800/40 rounded" />
        </div>
      )}

      {/* @block 1.4 Error */}
      {err && (
        <div className="rounded-xl border border-stall/40 bg-stall/10 p-5 text-sm text-stall">
          Failed to load engagements: {err}
        </div>
      )}

      {/* @block 1.5 Empty */}
      {rows && rows.length === 0 && !err && (
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-8 text-center">
          <div className="text-paper-100 font-semibold mb-1">No active client engagements yet</div>
          <p className="text-sm text-muted">
            Clients show up here once a deal in <NavLink to="/pipeline" className="text-accent-400 hover:underline">Pipeline</NavLink>
            {' '}hits Won and an engagement is opened against the account.
          </p>
        </div>
      )}

      {/* @block 1.6 Roster */}
      {rows && rows.length > 0 && (
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 overflow-hidden">
          <table className="proto-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Status</th>
                <th>Owner</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const id = r.client_id || r.id || String(i);
                const name = r.client_name || r.name || '(unnamed)';
                const status = r.status || '—';
                const owner = r.owner || r.owner_name || '—';
                const started = r.started_at ? new Date(r.started_at).toLocaleDateString() : '—';
                return (
                  <tr key={id}>
                    <td>
                      <NavLink to={`/clients/${id}`} className="text-paper-100 hover:text-accent-400 font-medium">
                        {name}
                      </NavLink>
                    </td>
                    <td className="muted">{status}</td>
                    <td>{owner}</td>
                    <td className="muted">{started}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
